import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useCitizenContext } from '@accenturecodefoundry/mfe-pot-citizen-context';
import { useLocale, useTranslations } from '@accenturecodefoundry/mfe-pot-shared-i18n';
import type { Skill, SortBy } from 'job-bank-data-access';
import type { RefineParams } from '../use-job-matches';
import { fetchSkills, addSkill, removeSkill } from '../data/skills-api';
import { assetBaseUrl } from '../asset-base-url';
import './RefinePanel.css';

const FOCUSABLE =
  'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [href], [tabindex]:not([tabindex="-1"])';

const PROVINCES: { code: string; en: string; fr: string }[] = [
  { code: 'AB', en: 'Alberta', fr: 'Alberta' },
  { code: 'BC', en: 'British Columbia', fr: 'Colombie-Britannique' },
  { code: 'MB', en: 'Manitoba', fr: 'Manitoba' },
  { code: 'NB', en: 'New Brunswick', fr: 'Nouveau-Brunswick' },
  { code: 'NL', en: 'Newfoundland and Labrador', fr: 'Terre-Neuve-et-Labrador' },
  { code: 'NS', en: 'Nova Scotia', fr: 'Nouvelle-Écosse' },
  { code: 'NT', en: 'Northwest Territories', fr: 'Territoires du Nord-Ouest' },
  { code: 'NU', en: 'Nunavut', fr: 'Nunavut' },
  { code: 'ON', en: 'Ontario', fr: 'Ontario' },
  { code: 'PE', en: 'Prince Edward Island', fr: 'Île-du-Prince-Édouard' },
  { code: 'QC', en: 'Quebec', fr: 'Québec' },
  { code: 'SK', en: 'Saskatchewan', fr: 'Saskatchewan' },
  { code: 'YT', en: 'Yukon', fr: 'Yukon' },
];

const JOB_TYPE_CHIPS: { id: string; labelKey: string }[] = [
  { id: 'full-time', labelKey: 'jobMatches.refinePanel.jobType.chips.fullTime' },
  { id: 'part-time', labelKey: 'jobMatches.refinePanel.jobType.chips.partTime' },
  { id: 'contract', labelKey: 'jobMatches.refinePanel.jobType.chips.contract' },
  { id: 'hybrid', labelKey: 'jobMatches.refinePanel.jobType.chips.hybrid' },
  { id: 'remote', labelKey: 'jobMatches.refinePanel.jobType.chips.remote' },
  { id: 'on-site', labelKey: 'jobMatches.refinePanel.jobType.chips.onSite' },
];

const SORT_OPTIONS: { value: SortBy; labelKey: string }[] = [
  { value: 'best-skills-fit', labelKey: 'jobMatches.refinePanel.sort.bestSkillsFit' },
  { value: 'most-recent', labelKey: 'jobMatches.refinePanel.sort.mostRecent' },
  { value: 'highest-pay', labelKey: 'jobMatches.refinePanel.sort.highestPay' },
];

function validateSkillInput(value: string, t: (k: string) => string): string | null {
  const trimmed = value.trim();
  if (trimmed.length < 2) return t('jobMatches.refinePanel.skills.errors.tooShort');
  if (trimmed.length > 50) return t('jobMatches.refinePanel.skills.errors.tooLong');
  if (/[<>"'`\n\r]/.test(value)) return t('jobMatches.refinePanel.skills.errors.invalidChars');
  return null;
}

export interface RefinePanelProps {
  onClose: () => void;
  onApply: (params: RefineParams) => void;
  initialValues?: Partial<RefineParams>;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
}

export function RefinePanel({ onClose, onApply, initialValues, triggerRef }: RefinePanelProps) {
  const panelRef = useRef<HTMLElement>(null);
  const locale = useLocale();
  const { t } = useTranslations(assetBaseUrl, locale);
  const { data: citizenData, status: citizenStatus } = useCitizenContext();

  const [address, setAddress] = useState(initialValues?.address ?? '');
  const [province, setProvince] = useState(initialValues?.province ?? '');
  const [jobTypes, setJobTypes] = useState<string[]>(initialValues?.jobTypes ?? []);
  const [sortBy, setSortBy] = useState<SortBy>(initialValues?.sortBy ?? 'best-skills-fit');
  const [skills, setSkills] = useState<Skill[] | null>(null);
  const [skillInput, setSkillInput] = useState('');
  const [skillError, setSkillError] = useState<string | null>(null);
  const [addingSkill, setAddingSkill] = useState(false);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());

  // Pre-fill address and province from citizen context only when no prior values exist
  useEffect(() => {
    if (citizenStatus === 'ready' && !initialValues?.address) {
      const city = citizenData?.address?.city ?? '';
      const prov = citizenData?.address?.province ?? '';
      setAddress([city, prov].filter(Boolean).join(', '));
      setProvince(prov);
    }
  }, [citizenStatus]);

  // Fetch skills when panel mounts (i.e. when it opens)
  useEffect(() => {
    let cancelled = false;
    fetchSkills().then((s) => {
      if (!cancelled) setSkills(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Focus trap + Escape key — runs once on mount
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const previousFocus = document.activeElement as HTMLElement | null;
    const firstFocusable = panel.querySelector<HTMLElement>(FOCUSABLE);
    firstFocusable?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => !el.hasAttribute('disabled'),
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      // Return focus to the button that opened the panel, or to the previously focused element
      (triggerRef.current ?? previousFocus)?.focus();
    };
  }, [onClose, triggerRef]);

  function toggleJobType(id: string) {
    setJobTypes((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function handleSkillInputChange(value: string) {
    setSkillInput(value);
    if (skillError) setSkillError(null);
  }

  async function handleAddSkill() {
    if (!skillInput.trim()) return;
    const error = validateSkillInput(skillInput, t);
    if (error) {
      setSkillError(error);
      return;
    }
    setAddingSkill(true);
    try {
      const newSkill = await addSkill(skillInput.trim());
      setSkills((prev) => [...(prev ?? []), newSkill]);
      setSkillInput('');
      setSkillError(null);
    } catch (err) {
      setSkillError(err instanceof Error ? err.message : t('jobMatches.refinePanel.skills.errors.tooLong'));
    } finally {
      setAddingSkill(false);
    }
  }

  async function handleRemoveSkill(id: string) {
    setRemovingIds((prev) => new Set([...prev, id]));
    try {
      await removeSkill(id);
      setSkills((prev) => (prev ?? []).filter((s) => s.id !== id));
    } finally {
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  function handleReset() {
    const city = citizenData?.address?.city ?? '';
    const prov = citizenData?.address?.province ?? '';
    setAddress([city, prov].filter(Boolean).join(', '));
    setProvince(prov);
    setJobTypes([]);
    setSortBy('best-skills-fit');
    // Skills are not reset — they are persisted and require explicit removal
  }

  function handleApply() {
    onApply({ address, province, jobTypes, sortBy, skills: skills ?? [] });
  }

  const addButtonDisabled = !skillInput.trim() || addingSkill;

  return (
    <div
      className="refine-panel__overlay"
      role="button"
      tabIndex={-1}
      aria-label={t('jobMatches.refinePanel.dismiss')}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClose(); }}
    >
      <aside
        ref={panelRef}
        className="refine-panel"
        aria-label={t('jobMatches.refinePanel.heading')}
      >
        {/* Header */}
        <div className="refine-panel__header">
          <h2 className="refine-panel__title">{t('jobMatches.refinePanel.heading')}</h2>
          <button
            type="button"
            className="refine-panel__close"
            onClick={onClose}
            aria-label={t('jobMatches.refinePanel.close')}
          >
            &times;
          </button>
        </div>

        <div className="refine-panel__body">
          {/* Address */}
          <div className="refine-panel__field">
            <label htmlFor="refine-address" className="refine-panel__label">
              {t('jobMatches.refinePanel.address.label')}
            </label>
            <input
              id="refine-address"
              type="text"
              className="refine-panel__input"
              value={address}
              placeholder={t('jobMatches.refinePanel.address.placeholder')}
              onChange={(e) => setAddress(e.currentTarget.value)}
            />
          </div>

          {/* Province */}
          <div className="refine-panel__field">
            <label htmlFor="refine-province" className="refine-panel__label">
              {t('jobMatches.refinePanel.province.label')}
            </label>
            <select
              id="refine-province"
              className="refine-panel__select"
              value={province}
              onChange={(e) => setProvince(e.currentTarget.value)}
            >
              <option value="">—</option>
              {PROVINCES.map((p) => (
                <option key={p.code} value={p.code}>
                  {locale === 'fr' ? p.fr : p.en}
                </option>
              ))}
            </select>
          </div>

          {/* Job type and work location chips */}
          <fieldset className="refine-panel__fieldset">
            <legend className="refine-panel__legend">{t('jobMatches.refinePanel.jobType.heading')}</legend>
            {/* TODO: pass jobType to BFF when server-side filtering is added */}
            <div className="refine-panel__chips">
              {JOB_TYPE_CHIPS.map((chip) => {
                const active = jobTypes.includes(chip.id);
                return (
                  <button
                    key={chip.id}
                    type="button"
                    className={`refine-panel__chip${active ? ' refine-panel__chip--active' : ''}`}
                    aria-pressed={active}
                    onClick={() => toggleJobType(chip.id)}
                  >
                    {t(chip.labelKey)}
                  </button>
                );
              })}
            </div>
          </fieldset>

          {/* Skills */}
          <section className="refine-panel__section">
            <h3 className="refine-panel__section-heading">{t('jobMatches.refinePanel.skills.heading')}</h3>
            <p className="refine-panel__section-subtitle">{t('jobMatches.refinePanel.skills.subtitle')}</p>

            {skills === null ? (
              <div className="refine-panel__skills-loading" aria-label={t('jobMatches.loading')} />
            ) : (
              <ul className="refine-panel__skill-tags">
                {skills.map((skill) => (
                  <li key={skill.id} className="refine-panel__skill-tag">
                    <span>{locale === 'fr' ? skill.label.fr : skill.label.en}</span>
                    <button
                      type="button"
                      className="refine-panel__skill-remove"
                      aria-label={t('jobMatches.refinePanel.skills.remove', {
                        skill: locale === 'fr' ? skill.label.fr : skill.label.en,
                      })}
                      disabled={removingIds.has(skill.id)}
                      onClick={() => handleRemoveSkill(skill.id)}
                    >
                      &times;
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="refine-panel__skill-add">
              <div className="refine-panel__skill-add-row">
                <input
                  type="text"
                  className={`refine-panel__input refine-panel__skill-input${skillError ? ' refine-panel__input--error' : ''}`}
                  placeholder={t('jobMatches.refinePanel.skills.addPlaceholder')}
                  value={skillInput}
                  onChange={(e) => handleSkillInputChange(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddSkill();
                    }
                  }}
                  aria-describedby={skillError ? 'skill-add-error' : undefined}
                  aria-invalid={skillError ? 'true' : undefined}
                />
                <button
                  type="button"
                  className="refine-panel__skill-add-btn"
                  disabled={addButtonDisabled}
                  onClick={handleAddSkill}
                >
                  {t('jobMatches.refinePanel.skills.addButton')}
                </button>
              </div>
              {skillError && (
                <p id="skill-add-error" className="refine-panel__skill-error" role="alert">
                  {skillError}
                </p>
              )}
            </div>
          </section>

          {/* Sort */}
          <fieldset className="refine-panel__fieldset">
            <legend className="refine-panel__legend">{t('jobMatches.refinePanel.sort.heading')}</legend>
            <div className="refine-panel__sort-options">
              {SORT_OPTIONS.map((opt) => (
                <label key={opt.value} className="refine-panel__sort-option">
                  <input
                    type="radio"
                    name="sort-by"
                    value={opt.value}
                    checked={sortBy === opt.value}
                    onChange={() => setSortBy(opt.value)}
                    className="refine-panel__radio"
                  />
                  {t(opt.labelKey)}
                </label>
              ))}
            </div>
          </fieldset>
        </div>

        {/* Footer */}
        <div className="refine-panel__footer">
          <button type="button" className="refine-panel__reset-btn" onClick={handleReset}>
            {t('jobMatches.refinePanel.reset')}
          </button>
          <button type="button" className="refine-panel__apply-btn" onClick={handleApply}>
            {t('jobMatches.refinePanel.apply')}
          </button>
        </div>
      </aside>
    </div>
  );
}
